import { useCallback } from "react";

import type { AirHostSettings } from "@/lib/gql/__generated__/graphql";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

import type { SiteControllerSettingBaseFormModel } from "../../../../base/type";
import type { SiteControllerSettingBaseFormModelOnSetViewModelType } from "../../../../base/use-case/use-create-model/use-create-model";
import type { SiteControllerSettingAirHostFormModel } from "../../../type";

type Props = {
	viewModel: SiteControllerSettingAirHostFormModel["ViewModel"];
	queryData: SiteControllerSettingBaseFormModel["QueryData"];
	onSetViewModel: SiteControllerSettingBaseFormModelOnSetViewModelType;
};

export type SiteControllerSettingAirHostFormModelMethodsType = {
	onSetAirHostAPIAccessToken: (input: string) => void;
	onSetAirHostPartnerID: (input: string) => void;
	onSetAirHostPropertyID: (input: string) => void;
	onAppendRoomMapping: () => void;
	onRemoveRoomMapping: (index: number) => void;
	onChangeRoomMappingChillnnRoomID: (value: string, index: number) => void;
	onChangeRoomMappingAirHostPlanID: (
		airHostPlanID: string, //
		airHostRoomTypeID: string,
		airHostRoomCheckInTime: string,
		airHostRoomCheckOutTime: string,
		index: number,
	) => void;
};

type UseSiteControllerSettingAirHostFormModelMethodsType = ({
	viewModel,
	onSetViewModel,
}: Props) => SiteControllerSettingAirHostFormModelMethodsType;

export const useSiteControllerSettingAirHostFormModelMethods: UseSiteControllerSettingAirHostFormModelMethodsType = ({
	viewModel,
	onSetViewModel,
}) => {
	const onSetAirHostAPIAccessToken = useCallback(
		(input: string) => {
			const { airHostSettings } = viewModel;
			const { airHostAPIAccessToken } = airHostSettings;

			const newValue: ForAutoGenerator<AirHostSettings, "hotelID"> = {
				...airHostSettings,
				airHostAPIAccessToken: {
					...airHostAPIAccessToken,
					value: input,
				},
			};

			onSetViewModel([
				{
					key: "airHostSettings",
					value: newValue,
				},
			]);
		},
		[onSetViewModel, viewModel],
	);

	const onSetAirHostPropertyID = useCallback(
		(value: string) => {
			const { airHostSettings } = viewModel;
			const { airHostPropertyID } = airHostSettings;

			const newValue: ForAutoGenerator<AirHostSettings, "hotelID"> = {
				...airHostSettings,
				airHostPropertyID: {
					...airHostPropertyID,
					value: value,
				},
			};

			onSetViewModel([
				{
					key: "airHostSettings",
					value: newValue,
				},
			]);
		},

		[onSetViewModel, viewModel],
	);

	const onSetAirHostPartnerID = useCallback(
		(value: string) => {
			const { airHostSettings } = viewModel;

			const newValue: ForAutoGenerator<AirHostSettings, "hotelID"> = {
				...airHostSettings,
				airHostPartnerID: {
					isEdited: true,
					value,
				},
			};

			onSetViewModel([
				{
					key: "airHostSettings",
					value: newValue,
				},
			]);
		},
		[onSetViewModel, viewModel],
	);

	const onAppendRoomMapping = useCallback(() => {
		const { airHostSettings } = viewModel;
		const { chillnnRoomAndAirHostPricePlanMappings } = airHostSettings;

		const newMapping = {
			chillnnRoomID: {
				isEdited: true,
				value: "",
			},
			airHostPlanID: {
				isEdited: true,
				value: "",
			},
			airHostRoomTypeID: {
				isEdited: true,
				value: "",
			},
			airHostRoomCheckInTime: {
				isEdited: true,
				value: "",
			},
			airHostRoomCheckOutTime: {
				isEdited: true,
				value: "",
			},
		};

		const newValue: ForAutoGenerator<AirHostSettings, "hotelID"> = {
			...airHostSettings,
			chillnnRoomAndAirHostPricePlanMappings: {
				...chillnnRoomAndAirHostPricePlanMappings,
				value: [...chillnnRoomAndAirHostPricePlanMappings.value, newMapping],
			},
		};

		onSetViewModel([
			{
				key: "airHostSettings",
				value: newValue,
			},
		]);
	}, [onSetViewModel, viewModel]);

	const onRemoveRoomMapping = useCallback(
		(index: number) => {
			const { airHostSettings } = viewModel;
			const { chillnnRoomAndAirHostPricePlanMappings } = airHostSettings;

			const newValue: ForAutoGenerator<AirHostSettings, "hotelID"> = {
				...airHostSettings,
				chillnnRoomAndAirHostPricePlanMappings: {
					...chillnnRoomAndAirHostPricePlanMappings,
					value: chillnnRoomAndAirHostPricePlanMappings.value.filter((_, i) => i !== index),
				},
			};

			onSetViewModel([
				{
					key: "airHostSettings",
					value: newValue,
				},
			]);
		},
		[onSetViewModel, viewModel],
	);

	const onChangeRoomMappingChillnnRoomID = useCallback(
		(value: string, index: number) => {
			const { airHostSettings } = viewModel;
			const { chillnnRoomAndAirHostPricePlanMappings } = airHostSettings;

			const newValue: ForAutoGenerator<AirHostSettings, "hotelID"> = {
				...airHostSettings,
				chillnnRoomAndAirHostPricePlanMappings: {
					...chillnnRoomAndAirHostPricePlanMappings,
					value: chillnnRoomAndAirHostPricePlanMappings.value.map((item, i) => {
						if (i === index) {
							return {
								...item,
								chillnnRoomID: {
									isEdited: true,
									value,
								},
							};
						}
						return item;
					}),
				},
			};

			onSetViewModel([
				{
					key: "airHostSettings",
					value: newValue,
				},
			]);
		},
		[onSetViewModel, viewModel],
	);

	const onChangeRoomMappingAirHostPlanID = useCallback(
		(
			airHostPlanID: string,
			airHostRoomTypeID: string,
			airHostRoomCheckInTime: string,
			airHostRoomCheckOutTime: string,
			index: number,
		) => {
			const { airHostSettings } = viewModel;
			const { chillnnRoomAndAirHostPricePlanMappings } = airHostSettings;

			const newValue: ForAutoGenerator<AirHostSettings, "hotelID"> = {
				...airHostSettings,
				chillnnRoomAndAirHostPricePlanMappings: {
					...chillnnRoomAndAirHostPricePlanMappings,
					value: chillnnRoomAndAirHostPricePlanMappings.value.map((item, i) => {
						if (i === index) {
							return {
								...item,
								airHostPlanID: {
									isEdited: true,
									value: airHostPlanID,
								},
								airHostRoomTypeID: {
									isEdited: true,
									value: airHostRoomTypeID,
								},
								airHostRoomCheckInTime: {
									isEdited: true,
									value: airHostRoomCheckInTime,
								},
								airHostRoomCheckOutTime: {
									isEdited: true,
									value: airHostRoomCheckOutTime,
								},
							};
						}
						return item;
					}),
				},
			};

			onSetViewModel([
				{
					key: "airHostSettings",
					value: newValue,
				},
			]);
		},
		[onSetViewModel, viewModel],
	);

	return {
		onSetAirHostAPIAccessToken,
		onSetAirHostPartnerID,
		onSetAirHostPropertyID,
		onAppendRoomMapping,
		onRemoveRoomMapping,
		onChangeRoomMappingChillnnRoomID,
		onChangeRoomMappingAirHostPlanID,
	};
};
